import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ClipboardList, ListChecks, MapPin, Boxes, Calculator, CalendarRange, Contact,
  ArrowRight, Hammer,
} from "lucide-react";
import ModelWorkAreas from "@/components/ModelWorkAreas";
import ModelActivities from "@/components/ModelActivities";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { key: "antes", q: "ANTES", concept: "Preliminares", icon: ClipboardList, kind: "soon",
    desc: "Datos y contenidos previos que usaremos en el resto de apartados. Lo definiremos más adelante." },
  { key: "que", q: "¿QUÉ?", concept: "Actividades", icon: ListChecks, kind: "actividades",
    desc: "Qué hay que hacer: el catálogo de actividades del proyecto." },
  { key: "donde", q: "¿DÓNDE?", concept: "Áreas de trabajo", icon: MapPin, kind: "areas",
    desc: "Dónde se actúa: las áreas de trabajo organizadas por niveles de la vivienda." },
  { key: "como", q: "¿CÓMO?", concept: "Recursos", icon: Boxes, kind: "link", to: "/admin/recursos",
    desc: "Con qué se hace: los recursos necesarios para desarrollar las actividades." },
  { key: "cuanto", q: "¿CUÁNTO?", concept: "Presupuesto", icon: Calculator, kind: "soon",
    desc: "Cuánto cuesta: valoración de todos los recursos desde distintos puntos de vista." },
  { key: "cuando", q: "¿CUÁNDO?", concept: "Gestión", icon: CalendarRange, kind: "soon",
    desc: "Cuándo se hace: agenda y diagrama de Gantt con la ubicación temporal de las actividades." },
  { key: "quien", q: "¿QUIÉN?", concept: "Contactos", icon: Contact, kind: "link", to: "/admin/crm",
    desc: "Quién interviene: contactos del CRM implicados en actividades o suministro de recursos." },
];

function SoonPanel({ pillar }) {
  const Icon = pillar.icon;
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center" data-testid={`pillar-soon-${pillar.key}`}>
      <div className="mx-auto h-12 w-12 rounded-xl bg-accent text-olive flex items-center justify-center mb-4">
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="text-xl font-semibold">{pillar.concept}</h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{pillar.desc}</p>
      <div className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-muted-foreground">
        <Hammer className="h-3.5 w-3.5" /> En construcción
      </div>
    </div>
  );
}

function LinkPanel({ pillar }) {
  const navigate = useNavigate();
  const Icon = pillar.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid={`pillar-link-${pillar.key}`}>
      <div className="mx-auto h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="text-xl font-semibold">{pillar.concept}</h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{pillar.desc}</p>
      <Button className="rounded-full gap-2 mt-5" onClick={() => navigate(pillar.to)} data-testid={`pillar-open-${pillar.key}`}>
        Abrir {pillar.concept} <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function ModelHome() {
  const [sp] = useSearchParams();
  const [active, setActive] = useState(sp.get("tab") || "donde");
  const initialQuery = sp.get("q") || "";
  useEffect(() => { const t = sp.get("tab"); if (t) setActive(t); }, [sp]);
  const current = PILLARS.find((p) => p.key === active) || PILLARS[2];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="model-home-page">
      <div className="mb-8">
        <div className="caption mb-2">Plantilla madre</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Vivienda modelo</h1>
        <p className="text-muted-foreground mt-2">La estructura base del proyecto, organizada por apartados.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sub-navegación de apartados */}
        <nav className="lg:col-span-3" data-testid="model-pillars-nav">
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 lg:sticky lg:top-24">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              const isActive = p.key === active;
              return (
                <button
                  key={p.key}
                  onClick={() => setActive(p.key)}
                  data-testid={`pillar-tab-${p.key}`}
                  className={`shrink-0 lg:w-full flex items-center gap-3 rounded-xl px-3.5 py-3 text-left transition-colors border ${
                    isActive ? "bg-primary/10 border-primary/30 text-primary" : "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${isActive ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground/70"}`}>
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold tracking-tight leading-none">{p.q}</div>
                    <div className="text-xs leading-tight mt-1 opacity-80 truncate">{p.concept}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Contenido del apartado */}
        <div className="lg:col-span-9 min-w-0" data-testid="model-pillar-content">
          {current.kind === "areas" && <ModelWorkAreas key={`donde-${initialQuery}`} initialQuery={initialQuery} />}
          {current.kind === "actividades" && <ModelActivities key={`que-${initialQuery}`} initialQuery={initialQuery} />}
          {current.kind === "link" && <LinkPanel pillar={current} />}
          {current.kind === "soon" && <SoonPanel pillar={current} />}
        </div>
      </div>
    </div>
  );
}
